import axios from "axios";
import React, { useCallback, useEffect, useState } from "react";

import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@material-ui/core";

import Loader from "./Loader";
import TableHeadCell from "./TableHeadCell";
import DeleteConfirmationDialog from "./DeleteConfirmationDialog";
import DeviceKFilterExceptionForm from "./DeviceKFilterExceptionForm";
import DeviceKFilterUpstreamForm from "./DeviceKFilterUpstreamForm";

const STATUS_FIELDS = {
  unit_serial: "Unit Serial",
  rule_status: "Rule Status",
  config_retries: "Config Retries",
  unit_registered: "Unit Registered",
  tunnel_interface: "Tunnel Interface",
  tunnel_status: "Tunnel Status",
  tunnel_connected: "Tunnel Connected",
  last_handshake: "Last Handshake",
};

const useStyles = makeStyles((theme) => ({
  heading: {
    fontSize: theme.typography.pxToRem(15),
    flexBasis: "50%",
    flexShrink: 0,
  },
  actions: {
    marginLeft: "auto",
    display: "flex",
  },
  actionButton: {
    marginLeft: theme.spacing(1),
  },
}));

const formatStatusValue = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
};

const normalizeException = (exception) => {
  if (typeof exception === "string") {
    return { ip_address: exception };
  }
  if (exception && typeof exception === "object") {
    return {
      ip_address: exception.ip_address || exception.ip || "",
      ...exception,
    };
  }
  return { ip_address: String(exception || "") };
};

const DeviceKFilter = (props) => {
  const classes = useStyles();
  const deviceSerial = props.deviceSerial || "UNIT12345";
  const { setError, setMessage } = props;
  const [loading, setLoading] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [openExceptionForm, setOpenExceptionForm] = useState(null);
  const [editingException, setEditingException] = useState(null);
  const [openUpstreamForm, setOpenUpstreamForm] = useState(null);
  const [status, setStatus] = useState(null);
  const [hasFilter, setHasFilter] = useState(false);
  const [exceptionsData, setExceptionsData] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedExceptionIp, setSelectedExceptionIp] = useState(null);

  const getStatus = useCallback(() => {
    return axios
      .get(`/device/kfilter/status/${deviceSerial}`)
      .then((response) => {
        setStatus(response.data);
        setHasFilter(true);
        return response.data;
      })
      .catch((error) => {
        if (error?.response?.status === 404) {
          setStatus(null);
          setHasFilter(false);
          return null;
        }
        setError(
          error?.response?.data?.detail || "Error fetching KFilter status"
        );
        return null;
      });
  }, [deviceSerial, setError]);

  const getExceptions = useCallback(() => {
    return axios
      .get(`/device/kfilter/exceptions/${deviceSerial}`)
      .then((response) => {
        setExceptionsData(response.data);
        return response.data;
      })
      .catch((error) => {
        if (error?.response?.status === 404) {
          setExceptionsData(null);
          return null;
        }
        setError(
          error?.response?.data?.detail || "Error fetching KFilter exceptions"
        );
        return null;
      });
  }, [deviceSerial, setError]);

  const refreshKFilter = useCallback(() => {
    setLoading(true);
    getStatus()
      .then((statusData) => {
        if (statusData) {
          return getExceptions();
        }
        setExceptionsData(null);
        return null;
      })
      .finally(() => {
        setLoading(false);
      });
  }, [getStatus, getExceptions]);

  useEffect(() => {
    refreshKFilter();
    // eslint-disable-next-line
  }, [deviceSerial]);

  const enableKFilter = () => {
    setLoading(true);
    axios
      .post(`/device/kfilter/enable/${deviceSerial}`)
      .then(() => {
        setMessage("KFilter enabled successfully.");
        refreshKFilter();
      })
      .catch((error) => {
        setError(error?.response?.data?.detail || "Error enabling KFilter");
        setLoading(false);
      });
  };

  const disableKFilter = () => {
    setLoading(true);
    axios
      .post(`/device/kfilter/disable/${deviceSerial}`)
      .then(() => {
        setMessage("KFilter disabled successfully.");
        refreshKFilter();
      })
      .catch((error) => {
        setError(error?.response?.data?.detail || "Error disabling KFilter");
        setLoading(false);
      });
  };

  const deleteException = (ipAddress) => {
    setLoading(true);
    axios
      .delete(
        `/device/kfilter/exception/${deviceSerial}/${encodeURIComponent(
          ipAddress
        )}`
      )
      .then(() => {
        setMessage("Deleted KFilter exception successfully.");
        return getExceptions();
      })
      .catch((error) => {
        setError(
          error?.response?.data?.detail || "Error deleting KFilter exception"
        );
      })
      .finally(() => {
        setLoading(false);
        setSelectedExceptionIp(null);
      });
  };

  const isEnabled =
    hasFilter && String(status?.rule_status || "").toLowerCase() === "enabled";
  const exceptions = (exceptionsData?.exceptions || []).map(normalizeException);
  const ruleId = exceptionsData?.rule_id;

  if (loading) return <Loader />;

  return (
    <>
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedExceptionIp(null);
        }}
        onConfirm={() => {
          deleteException(selectedExceptionIp);
          setDeleteDialogOpen(false);
        }}
      />

      <Dialog
        open={Boolean(confirmAction)}
        onClose={() => setConfirmAction(null)}
      >
        <DialogTitle>
          {confirmAction === "enable" ? "Enable KFilter" : "Disable KFilter"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmAction === "enable"
              ? "Do you want to enable KFilter for this device?"
              : "Do you want to disable KFilter for this device?"}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              confirmAction === "enable" ? enableKFilter() : disableKFilter();
              setConfirmAction(null);
            }}
            style={{
              backgroundColor:
                confirmAction === "enable" ? undefined : "#d32f2f",
              color: confirmAction === "enable" ? undefined : "#fff",
            }}
            color="primary"
            variant="contained"
          >
            {confirmAction === "enable" ? "Enable" : "Disable"}
          </Button>
          <Button
            onClick={() => setConfirmAction(null)}
            color="primary"
            variant="outlined"
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {openExceptionForm && (
        <DeviceKFilterExceptionForm
          deviceSerial={deviceSerial}
          ruleId={ruleId}
          initialData={
            editingException
              ? { ip_address: editingException.ip_address }
              : { ip_address: "" }
          }
          existingIp={editingException?.ip_address}
          setError={setError}
          setMessage={setMessage}
          open={openExceptionForm}
          handleOpen={(value) => {
            setOpenExceptionForm(value);
            if (!value) setEditingException(null);
          }}
          callback={getExceptions}
        />
      )}

      {openUpstreamForm && (
        <DeviceKFilterUpstreamForm
          deviceSerial={deviceSerial}
          setError={setError}
          setMessage={setMessage}
          open={openUpstreamForm}
          handleOpen={setOpenUpstreamForm}
        />
      )}

      <Box>
        <Paper style={{ padding: "16px", marginBottom: "16px" }}>
          <Typography variant="subtitle1" gutterBottom>
            KFilter Status
          </Typography>
          <Grid container spacing={2} style={{ marginBottom: "12px" }}>
            <Grid item>
              {!isEnabled ? (
                <Button
                  onClick={() => setConfirmAction("enable")}
                  color="primary"
                  variant="contained"
                >
                  Enable KFilter
                </Button>
              ) : (
                <Button
                  onClick={() => setConfirmAction("disable")}
                  style={{ backgroundColor: "#d32f2f", color: "#fff" }}
                  variant="contained"
                >
                  Disable KFilter
                </Button>
              )}
            </Grid>
            <Grid item>
              <Button
                onClick={refreshKFilter}
                color="primary"
                variant="outlined"
              >
                Refresh Status
              </Button>
            </Grid>
          </Grid>

          {!hasFilter ? (
            <Typography color="textSecondary">
              KFilter is not configured for this device.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small" aria-label="KFilter status table">
                <TableBody>
                  {Object.keys(STATUS_FIELDS).map((field) => (
                    <TableRow key={field}>
                      <TableHeadCell>{STATUS_FIELDS[field]}</TableHeadCell>
                      <TableCell>
                        {formatStatusValue(status?.[field])}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        <Paper style={{ padding: "16px", marginBottom: "16px" }}>
          <Typography variant="subtitle1" gutterBottom>
            KFilter Exceptions
          </Typography>
          <Grid item>
            <Button
              onClick={() => {
                setEditingException(null);
                setOpenExceptionForm(true);
              }}
              color="primary"
              variant="contained"
              style={{ marginBottom: "12px" }}
              disabled={!ruleId}
            >
              Add Exception
            </Button>
          </Grid>

          {!ruleId && (
            <Typography color="textSecondary" style={{ marginBottom: "8px" }}>
              Exceptions are available after KFilter is enabled and a rule is
              present.
            </Typography>
          )}

          {exceptions.length === 0 && ruleId && (
            <Typography color="textSecondary">
              No exceptions configured.
            </Typography>
          )}

          {exceptions.map((exception) => (
            <Accordion key={exception.ip_address}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`exception-${exception.ip_address}-content`}
                id={`exception-${exception.ip_address}`}
              >
                <Typography className={classes.heading}>
                  {`IP: ${exception.ip_address}`}
                </Typography>
                <div className={classes.actions}>
                  <Button
                    className={classes.actionButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingException(exception);
                      setOpenExceptionForm(true);
                    }}
                    color="primary"
                    variant="contained"
                    startIcon={<EditIcon />}
                  >
                    Edit
                  </Button>
                  <Button
                    className={classes.actionButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedExceptionIp(exception.ip_address);
                      setDeleteDialogOpen(true);
                    }}
                    style={{
                      backgroundColor: "#d32f2f",
                      color: "#fff",
                    }}
                    variant="contained"
                    startIcon={<DeleteIcon />}
                  >
                    Delete
                  </Button>
                </div>
              </AccordionSummary>
              <AccordionDetails>
                <TableContainer component={Paper}>
                  <Table aria-label="KFilter exception table">
                    <TableBody>
                      <TableRow>
                        <TableHeadCell>IP Address</TableHeadCell>
                        <TableCell>{exception.ip_address}</TableCell>
                      </TableRow>
                      {ruleId && (
                        <TableRow>
                          <TableHeadCell>Rule ID</TableHeadCell>
                          <TableCell>{ruleId}</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          ))}
        </Paper>

        <Paper style={{ padding: "16px" }}>
          <Typography variant="subtitle1" gutterBottom>
            KFilter Upstream Credentials
          </Typography>
          <Grid item>
            <Button
              onClick={() => setOpenUpstreamForm(true)}
              color="primary"
              variant="contained"
            >
              Set Upstream Credentials
            </Button>
          </Grid>
        </Paper>
      </Box>
    </>
  );
};

export default DeviceKFilter;
